import { consumeInterviewEventStream, ToolTraceEvent } from "@/api/sseParser";
import request from "@/request";

export interface PublicMetricSpace {
  id: string;
  slug: string;
  title: string;
  description?: string;
  snapshotJson: string;
}
export interface PublicMetricSpaceListItem {
  id: string; slug: string; title: string; description?: string; status: string;
  participantCount: number; confirmedCount: number; estimatedMinutes: number; publishedAt: string;
  publisherName?: string; publisherAvatar?: string;
}

export interface PurposeProgress {
  current: number;
  total: number;
  ratio: number;
}
export interface InterviewResult {
  sessionId: string;
  status: "ACTIVE" | "READY_FOR_CONFIRMATION" | "CONFIRMED";
  message: string;
  suggestedAnswers: string[];
  profileDraftJson?: string;
  replayed: boolean;
  purposeProgress?: PurposeProgress;
  agentMode?: "MODEL" | "DETERMINISTIC_FALLBACK";
}
export interface GroupTendency {
  sampleSize: number;
  stanceDistribution: Record<string, number>;
  averageConfidence: number;
  topFactors: Record<string, number>;
  topConcerns: Record<string, number>;
  commonGround: Record<string, number>;
  minorityViews: Record<string, number>;
  dimensions: DimensionAggregate[];
}
export interface DimensionAggregate {
  name: string;
  evidenceCount: number;
  averageStrength: number;
  summaries: string[];
}
export interface DecisionAction {
  id: string; title: string; rationale: string;
  evidenceType: "COMMON_GROUND" | "TOP_CONCERN" | "MINORITY_VIEW" | "CREATOR";
  evidenceLabel: string; evidenceCount: number;
  status: "PENDING" | "ACCEPTED" | "DEFERRED" | "REJECTED";
  creatorNote: string; version: number;
}
export interface SpaceDecision {
  spaceId: string; status: "DRAFT" | "COMPLETED";
  conclusionText: string; version: number; actions: DecisionAction[];
}

type ApiResponse<T> = { code: number; data: T; message: string };

export const getPublicSpace = (slug: string) =>
  request<ApiResponse<PublicMetricSpace>>(`/api/spaces/public/${slug}`);
export const listPublicSpaces = (limit = 20) =>
  request<ApiResponse<PublicMetricSpaceListItem[]>>(`/api/spaces/public`, { params: { limit } });

export const startInterview = (slug: string) =>
  request<ApiResponse<InterviewResult>>(`/api/interviews/spaces/${slug}`, {
    method: "POST",
  });

export const answerInterview = (
  sessionId: string,
  clientMessageId: string,
  answer: string
) =>
  request<ApiResponse<InterviewResult>>(
    `/api/interviews/${sessionId}/answers`,
    { method: "POST", data: { clientMessageId, answer } }
  );

export const confirmProfile = (sessionId: string, profileJson: string) =>
  request<ApiResponse<{ id: string; spaceId: string; status: string; profileJson: string; actorType: "USER" | "VISITOR" }>>(
    `/api/profiles/interviews/${sessionId}/confirm`,
    { method: "POST", data: { profileJson } }
  );

export const getGroupTendency = (slug: string) =>
  request<ApiResponse<GroupTendency>>(`/api/spaces/public/${slug}/tendency`);

export const getSpaceDecision = (spaceId: string) =>
  request<ApiResponse<SpaceDecision>>(`/api/spaces/${spaceId}/decision`);
export const updateDecisionAction = (
  spaceId: string, actionId: string,
  body: { status: string; creatorNote: string; version: number; idempotencyKey: string }
) => request<ApiResponse<DecisionAction>>(`/api/spaces/${spaceId}/decision/actions/${actionId}`, {
  method: "PUT", data: body,
});
export const saveSpaceConclusion = (
  spaceId: string,
  body: { conclusionText: string; complete: boolean; version: number; idempotencyKey: string }
) => request<ApiResponse<SpaceDecision>>(`/api/spaces/${spaceId}/decision/conclusion`, {
  method: "PUT", data: body,
});

export interface MyMetricSpaceRecord {
  id: string; slug: string; title: string; description?: string;
  status: string; decisionStatus: string; createTime?: string;
}
export interface MyMetricSpaceRecords { created: MyMetricSpaceRecord[]; participated: MyMetricSpaceRecord[]; }
export const listMyMetricSpaceRecords = () =>
  request<ApiResponse<MyMetricSpaceRecords>>("/api/spaces/mine");

export interface SpaceConfigVersion { version: number; snapshotJson: string; createdAt: string; }
export const confirmSpaceConfigVersion = (spaceId: string, snapshotJson: string, idempotencyKey: string) =>
  request<ApiResponse<SpaceConfigVersion>>(`/api/spaces/${spaceId}/config-versions/confirm`, {
    method: "POST", data: { snapshotJson, idempotencyKey },
  });

const streamInterview = async (
  url: string,
  body: Record<string, unknown> | undefined,
  onProgress: (stage: string) => void,
  onTool: (tool: ToolTraceEvent) => void
) => {
  const response = await fetch(url, {
    method: "POST",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    credentials: "include",
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new Error(payload?.message || "访谈请求失败");
  }
  if (!(response.headers.get("content-type") || "").includes("text/event-stream")) {
    throw new Error("访谈 Agent 返回了无效响应");
  }
  return consumeInterviewEventStream<InterviewResult>(response.body, onProgress, onTool);
};

export const startInterviewStream = (
  slug: string,
  onProgress: (stage: string) => void = () => undefined,
  onTool: (tool: ToolTraceEvent) => void = () => undefined
) => streamInterview(`/api/interviews/spaces/${encodeURIComponent(slug)}/stream`, undefined, onProgress, onTool);

export const answerInterviewStream = (
  sessionId: string,
  clientMessageId: string,
  answer: string,
  onProgress: (stage: string) => void = () => undefined,
  onTool: (tool: ToolTraceEvent) => void = () => undefined
) => streamInterview(`/api/interviews/${sessionId}/answers/stream`, { clientMessageId, answer }, onProgress, onTool);
